import React, { useState, useEffect, useRef } from 'react';
import { Button, Input } from 'semantic-ui-react';
import { EditorState, Modifier } from 'draft-js';
import { getSelectedBlocksMetadata } from 'draftjs-utils';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ResetIcon } from '../../../assets/images/toolbarIcons/reset.svg';

const Spacing = props => {
  const { t } = useTranslation();
  const enabledEditorId = useSelector(state => state.design.enabledEditorId);
  const editorState = useSelector(state => state.design.editorsState[props.entityId]);
  const editorsState = useSelector(state => state.design.editorsState);
  const [letterSpacing, setLetterSpacing] = useState();
  const [lineHeight, setLineHeight] = useState();
  const [sliderBox, setSliderBox] = useState();
  const resetRef = useRef(false);

  // Set needed parameters for position correcting
  const xRegex = /(?:translateX\()([0-9.-]+)/;
  const yRegex = /(?:translateY\()([0-9.-]+)/;
  const rRegex = /(?:rotate\()([0-9.-]+)/;
  const sRegex = /(?:scaleX\()([0-9.-]+)/;

  // End Set needed parameters for position correcting

  useEffect(() => {
    if (editorState) {
      const letterSpacing = getSelectedBlocksMetadata(editorState).get('letter-spacing');
      const lineHeight = getSelectedBlocksMetadata(editorState).get('line-height');
      setLetterSpacing((letterSpacing ? letterSpacing : 0) * 100);
      setLineHeight(lineHeight ? lineHeight : 1.4);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorState]);

  useEffect(() => {
    if (resetRef.current) {
      mouseUpHandler();
      resetRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [letterSpacing, lineHeight]);

  // Calculate actual position of corner A (top,left)
  const cornerA = (target, targetHolder) => {
    const xM = parseFloat(xRegex.exec(targetHolder.style.transform)[1]);
    const yM = parseFloat(yRegex.exec(targetHolder.style.transform)[1]);
    const rM = rRegex.exec(targetHolder.style.transform)[1];
    const sM = sRegex.exec(targetHolder.style.transform)[1];
    const rotate = (rM * Math.PI) / 180;
    const width = target.offsetWidth;
    const height = target.offsetHeight;
    const xA =
      parseFloat(xM + width / 2 + (width / -2) * (Math.cos(rotate) * sM)) -
      (height / -2) * (Math.sin(rotate) * sM);
    const yA =
      yM +
      height / 2 +
      (width / -2) * (Math.sin(rotate) * sM) +
      (height / -2) * (Math.cos(rotate) * sM);
    return { x: xM, y: yM, xA: xA, yA: yA, rM: rM, sM: sM };
  };

  // Get old position of 'A' corner position and caculate move pixels after
  // calculate new actual position and set to entity
  const entityNewPos = (target, targetHolder, oldAPos) => {
    targetHolder.style.height = target.offsetHeight + 'px';
    const newAPos = cornerA(target, targetHolder);
    const xDiff = newAPos.xA - oldAPos.xA;
    const yDiff = newAPos.yA - oldAPos.yA;

    const newX = parseFloat(newAPos.x - xDiff).toFixed(4);
    const newY = parseFloat(newAPos.y - yDiff).toFixed(4);
    const transform = `translateZ(5px) translateX(${newX}px) translateY(${newY}px) rotate(${newAPos.rM}deg) scaleX(${newAPos.sM}) scaleY(${newAPos.sM})`;
    targetHolder.style.transform = transform;
    if (target.style.transform) {
      target.style.transform = transform;
    }
  };

  // Set new inline styles to Draft blocks
  const applyStyle = (target, value, style) => {
    if (!target) return;
    const divs = target.querySelectorAll('div');
    const divsList = Array.apply(null, divs);

    let tempEditorState = editorState;
    let selectionKey, focusedKey, isBackward;
    let multiLines = 0;
    if (props.entityId === enabledEditorId) {
      let selection = tempEditorState.getSelection();
      selectionKey = selection._map._root.entries[0][1];
      focusedKey = selection._map._root.entries[2][1];
      isBackward = selection._map._root.entries[4][1];
      if (selectionKey !== focusedKey) {
        multiLines = 1;
      }
    }
    if (isBackward) divsList.reverse();
    divsList.forEach(div => {
      if (!value) {
        div.style.letterSpacing = '';
        div.style.lineHeight = '';
      } else {
        if (div.dataset.block) {
          if (selectionKey) {
            let divKey = div.dataset.offsetKey.split('-')[0];
            if (divKey === selectionKey) {
              if (style === 'letterSpacing') div.style.letterSpacing = `${value / 100}em`;
              if (style === 'lineHeight') div.style.lineHeight = `${value}`;
              if (multiLines === 1) multiLines = 2;
            }
            if (multiLines === 2) {
              if (style === 'letterSpacing') div.style.letterSpacing = `${value / 100}em`;
              if (style === 'lineHeight') div.style.lineHeight = `${value}`;
              if (divKey === focusedKey) multiLines = 0;
            }
          } else {
            if (style === 'letterSpacing') div.style.letterSpacing = `${value / 100}em`;
            if (style === 'lineHeight') div.style.lineHeight = `${value}`;
          }
        }
      }
    });
  };

  const letterHandleChange = (e, { value }) => {
    const entities = props.ids ? props.ids : [props.entityId];
    entities.forEach(entityId => {
      console.log(entityId);
      const target = document.getElementById(`ET_${entityId}`);
      const targetHolder = document.getElementById(`_${entityId}_`);
      const oldAPos = cornerA(target, targetHolder);
      applyStyle(target, value, 'letterSpacing');
      setLetterSpacing(value);
      entityNewPos(target, targetHolder, oldAPos);
    });
  };

  const lineHandleChange = (e, { value }) => {
    const entities = props.ids ? props.ids : [props.entityId];
    entities.forEach(entityId => {
      console.log(entityId);
      const target = document.getElementById(`ET_${entityId}`);
      const targetHolder = document.getElementById(`_${entityId}_`);
      const oldAPos = cornerA(target, targetHolder);
      applyStyle(target, value, 'lineHeight');
      setLineHeight(value);
      entityNewPos(target, targetHolder, oldAPos);
    });
  };

  const mouseUpHandler = () => {
    if (props.ids) applyToGroup();
    else {
      let tempEditorState = editorState;
      if (props.entityId !== enabledEditorId) tempEditorState = props.selectAll(editorState);
      const newContentState = Modifier.mergeBlockData(
        tempEditorState.getCurrentContent(),
        tempEditorState.getSelection(),
        { 'letter-spacing': letterSpacing / 100, 'line-height': parseFloat(lineHeight) },
      );
      let newState = EditorState.push(tempEditorState, newContentState, 'change-block-data');
      if (props.entityId !== enabledEditorId) newState = EditorState.moveSelectionToEnd(newState);
      props.onChange(newState);
    }

    applyStyle(null, null, '');
    // Save changes entity inline style to redux
    // let data = {
    //   master: css2js(targetHolder.style.cssText),
    // };
    // dispatch(designActions.changeEntityStyle(activePageRef.current, props.entityId, data));
  };

  const applyToGroup = () => {
    const newEditorsState = {};
    props.ids.forEach(id => {
      newEditorsState[id] = props.selectAll(editorsState[id]);
      const newContentState = Modifier.mergeBlockData(
        newEditorsState[id].getCurrentContent(),
        newEditorsState[id].getSelection(),
        { 'letter-spacing': letterSpacing / 100, 'line-height': parseFloat(lineHeight) },
      );
      let newState = EditorState.push(newEditorsState[id], newContentState, 'change-block-data');
      if (newState !== newEditorsState[id]) {
        newState = EditorState.moveSelectionToEnd(newState);
        newEditorsState[id] = newState;
      }
    });

    props.onChange(newEditorsState);
  };

  const toggleSlider = () => {
    if (sliderBox) {
      setSliderBox(false);
    } else {
      setSliderBox(true);
    }
  };

  const useOutsideClick = ref => {
    const handleClickOutside = event => {
      if (ref.current && !ref.current.contains(event.target)) {
        if (sliderBox) {
          setSliderBox(false);
        }
      }
    };

    useEffect(() => {
      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // Unbind the event listener on  clean up
        document.removeEventListener('mousedown', handleClickOutside);
      };
    });
  };

  const wrapperRef = useRef(null);
  useOutsideClick(wrapperRef);

  return (
    <>
      <Button onMouseDown={toggleSlider}>{t('spacing')}</Button>
      {sliderBox && (
        <div className="sliderBox" ref={wrapperRef}>
          <div className="sliderGroup">
            <span className="title">{t('letters')}</span>
            <Input
              label={parseInt(letterSpacing)}
              min={-20}
              max={200}
              name="letterSpacing"
              onChange={letterHandleChange}
              onMouseUp={mouseUpHandler}
              step={1}
              type="range"
              value={letterSpacing}
            />
            <div
              className="resetBtn"
              title="Reset"
              onClick={() => {
                resetRef.current = true;
                letterHandleChange(null, { value: 0 });
              }}
            >
              <ResetIcon />
            </div>
          </div>
          <div className="sliderGroup">
            <span className="title">{t('lineHeight')}</span>
            <Input
              label={lineHeight}
              min={0.5}
              max={2.5}
              name="lineHeight"
              onChange={lineHandleChange}
              onMouseUp={mouseUpHandler}
              step={0.01}
              type="range"
              value={lineHeight}
            />
            <div
              className="resetBtn"
              title="Reset"
              onClick={() => {
                resetRef.current = true;
                lineHandleChange(null, { value: 1.4 });
              }}
            >
              <ResetIcon />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

Spacing.propTypes = {
  entityId: PropTypes.number,
  onChange: PropTypes.func,
  selectAll: PropTypes.func,
  ids: PropTypes.any,
};

export default Spacing;
